import { MarketLimitsPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketLimits/MarketLimits';
import { AppState } from 'src/AppState/AppState';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { ChangeEvent } from 'react';
import { Amount } from 'src_common/common/amount/Amount';

export function moneyForView(stake: number): number {
    return stake / 100;
}

export function moneyForPayload(stake: string): number {
    return $appState.configState.precision.valueOldFormat(new Amount(stake));
}

function isValid(value: string): string | null {
    if (isNaN(Number(value)) === true) {
        return 'Only digits are allowed.';
    } else {
        return null;
    }
}

export class MarketLimitsState {
    @observable prematchMaxBetValue: string | null = null;
    @observable prematchMaxBetError: string | null = null;
    @observable prematchLiabilityLimitValue: string | null = null;
    @observable prematchLiabilityLimitError: string | null = null;
    @observable prematchMaxMultipleBetValue: string | null = null;
    @observable prematchMaxMultipleBetError: string | null = null;
    @observable prematchMaxWinValue: string | null = null;
    @observable prematchMaxWinError: string | null = null;

    @observable showMaxBetValue: string | null = null;
    @observable showMaxBetError: string | null = null;
    @observable showLiabilityLimitValue: string | null = null;
    @observable showLiabilityLimitError: string | null = null;
    @observable showMaxMultipleBetValue: string | null = null;
    @observable showMaxMultipleBetError: string | null = null;
    @observable showMaxWinValue: string | null = null;
    @observable showMaxWinError: string | null = null;

    @observable inPlayMaxBetValue: string | null = null;
    @observable inPlayMaxBetError: string | null = null;
    @observable inPlayLiabilityLimitValue: string | null = null;
    @observable inPlayLiabilityLimitError: string | null = null;
    @observable inPlayMaxMultipleBetValue: string | null = null;
    @observable inPlayMaxMultipleBetError: string | null = null;
    @observable inPlayMaxWinValue: string | null = null;
    @observable inPlayMaxWinError: string | null = null;

    constructor(
        readonly appState: AppState,
        readonly props: MarketLimitsPropsType
    ) {
        makeAutoObservable(this);
    }

    @computed get activeTab(): string {
        //TODO: change tab if event status was changed
        //const marketModel = this.appState.sdkStaff.models.getMarket(this.props.marketId);
        //if (marketModel === null) {
        //    return 'Prematch';
        //}
        //const eventModel = this.appState.sdkStaff.models.getEvent(marketModel.eventId);
        //if (eventModel === null) {
        //    return 'Prematch';
        //}

        //switch (eventModel.state) {
        //case 'resulted' :
        //    return 'InPlay';
        //case 'open' || 'new':
        //    return 'Prematch';
        //case 'settled':
        //    return 'Show';
        //}

        return 'Prematch';
    }

    @computed get prematchMaxBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.prematch.maxBet === undefined) {
            console.warn('Prematch max bet value is undefined');
        }
        const maxBet =
            marketModel.betLimitsMap?.prematch.maxBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.prematch.maxBet).value
                : '0';
        return this.prematchMaxBetValue !== null ? this.prematchMaxBetValue : maxBet;
    }

    @action onChangePrematchMaxBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.prematchMaxBetError = isValid(value);
        this.prematchMaxBetValue = event.target.value;
    };

    @action onSavePrematchMaxBet = (): void => {
        if (this.prematchMaxBetError !== null || this.prematchMaxBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                prematch: {
                    maxBet: moneyForPayload(this.prematchMaxBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get prematchLiabilityLimit(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.prematch.liabilityLimit === undefined) {
            console.warn('Prematch liability limit value is undefined');
        }

        const liabilityLimit =
            marketModel.betLimitsMap?.prematch.liabilityLimit !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.prematch.liabilityLimit).value
                : '0';
        return this.prematchLiabilityLimitValue !== null ? this.prematchLiabilityLimitValue : liabilityLimit;
    }

    @action onChangePrematchLiabilityLimit = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.prematchLiabilityLimitError = isValid(value);
        this.prematchLiabilityLimitValue = event.target.value;
    };

    @action onSavePrematchLiabilityLimit = (): void => {
        if (this.prematchLiabilityLimitError !== null || this.prematchLiabilityLimitValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                prematch: {
                    liabilityLimit: moneyForPayload(this.prematchLiabilityLimitValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get prematchMaxMultipleBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.prematch.maxMultipleBet === undefined) {
            console.warn('Prematch max multiple bet value is undefined');
        }

        const maxMultiple =
            marketModel.betLimitsMap?.prematch.maxMultipleBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.prematch.maxMultipleBet).value
                : '0';
        return this.prematchMaxMultipleBetValue !== null ? this.prematchMaxMultipleBetValue : maxMultiple;
    }

    @action onChangePrematchMaxMultipleBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.prematchMaxMultipleBetError = isValid(value);
        this.prematchMaxMultipleBetValue = event.target.value;
    };

    @action onSavePrematchMaxMultipleBet = (): void => {
        if (this.prematchMaxMultipleBetError !== null || this.prematchMaxMultipleBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                prematch: {
                    maxMultipleBet: moneyForPayload(this.prematchMaxMultipleBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get prematchMaxWin(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.prematch.maxWin === undefined) {
            console.warn('Prematch max win value is undefined');
        }

        const maxWin =
            marketModel.betLimitsMap?.prematch.maxWin !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.prematch.maxWin).value
                : '0';
        return this.prematchMaxWinValue !== null ? this.prematchMaxWinValue : maxWin;
    }

    @action onChangePrematchMaxWin = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.prematchMaxWinError = isValid(value);
        this.prematchMaxWinValue = event.target.value;
    };

    @action onSavePrematchMaxWin = (): void => {
        if (this.prematchMaxWinError !== null || this.prematchMaxWinValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                prematch: {
                    maxWin: moneyForPayload(this.prematchMaxWinValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get showMaxBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.show.maxBet === undefined) {
            console.warn('Show max bet value is undefined');
        }

        const maxBet =
            marketModel.betLimitsMap?.show.maxBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.show.maxBet).value
                : '0';
        return this.showMaxBetValue !== null ? this.showMaxBetValue : maxBet;
    }

    @action onChangeShowMaxBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.showMaxBetError = isValid(value);
        this.showMaxBetValue = event.target.value;
    };

    @action onSaveShowMaxBet = (): void => {
        if (this.showMaxBetError !== null || this.showMaxBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                show: {
                    maxBet: moneyForPayload(this.showMaxBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get showLiabilityLimit(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.show.liabilityLimit === undefined) {
            console.warn('Show liability limit value is undefined');
        }

        const liabilityLimit =
            marketModel.betLimitsMap?.show.liabilityLimit !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.show.liabilityLimit).value
                : '0';
        return this.showLiabilityLimitValue !== null ? this.showLiabilityLimitValue : liabilityLimit;
    }

    @action onChangeShowLiabilityLimit = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.showLiabilityLimitError = isValid(value);
        this.showLiabilityLimitValue = event.target.value;
    };

    @action onSaveShowLiabilityLimit = (): void => {
        if (this.showLiabilityLimitError !== null || this.showLiabilityLimitValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                show: {
                    liabilityLimit: moneyForPayload(this.showLiabilityLimitValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get showMaxMultipleBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.show.maxMultipleBet === undefined) {
            console.warn('Show max multiple bet value is undefined');
        }

        const maxMultiple =
            marketModel.betLimitsMap?.show.maxMultipleBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.show.maxMultipleBet).value
                : '0';
        return this.showMaxMultipleBetValue !== null ? this.showMaxMultipleBetValue : maxMultiple;
    }

    @action onChangeShowMaxMultipleBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.showMaxMultipleBetError = isValid(value);
        this.showMaxMultipleBetValue = event.target.value;
    };

    @action onSaveShowMaxMultipleBet = (): void => {
        if (this.showMaxMultipleBetError !== null || this.showMaxMultipleBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                show: {
                    maxMultipleBet: moneyForPayload(this.showMaxMultipleBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get showMaxWin(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.show.maxWin === undefined) {
            console.warn('Show max win value is undefined');
        }

        const maxWin =
            marketModel.betLimitsMap?.show.maxWin !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.show.maxWin).value
                : '0';
        return this.showMaxWinValue !== null ? this.showMaxWinValue : maxWin;
    }

    @action onChangeShowMaxWin = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.showMaxWinError = isValid(value);
        this.showMaxWinValue = event.target.value;
    };

    @action onSaveShowMaxWin = (): void => {
        if (this.showMaxWinError !== null || this.showMaxWinValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                show: {
                    maxWin: moneyForPayload(this.showMaxWinValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get inPlayMaxBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.inPlay.maxBet === undefined) {
            console.warn('InPlay max bet value is undefined');
        }

        const maxBet =
            marketModel.betLimitsMap?.inPlay.maxBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.inPlay.maxBet).value
                : '0';
        return this.inPlayMaxBetValue !== null ? this.inPlayMaxBetValue : maxBet;
    }

    @action onChangeInPlayMaxBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.inPlayMaxBetError = isValid(value);
        this.inPlayMaxBetValue = event.target.value;
    };

    @action onSaveInPlayMaxBet = (): void => {
        if (this.inPlayMaxBetError !== null || this.inPlayMaxBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                inPlay: {
                    maxBet: moneyForPayload(this.inPlayMaxBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get inPlayLiabilityLimit(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.inPlay.liabilityLimit === undefined) {
            console.warn('InPlay liability limit value is undefined');
        }

        const liabilityLimit =
            marketModel.betLimitsMap?.inPlay.liabilityLimit !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.inPlay.liabilityLimit).value
                : '0';
        return this.inPlayLiabilityLimitValue !== null ? this.inPlayLiabilityLimitValue : liabilityLimit;
    }

    @action onChangeInPlayLiabilityLimit = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.inPlayLiabilityLimitError = isValid(value);
        this.inPlayLiabilityLimitValue = event.target.value;
    };

    @action onSaveInPlayLiabilityLimit = (): void => {
        if (this.inPlayLiabilityLimitError !== null || this.inPlayLiabilityLimitValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                inPlay: {
                    liabilityLimit: moneyForPayload(this.inPlayLiabilityLimitValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get inPlayMaxMultipleBet(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.inPlay.maxMultipleBet === undefined) {
            console.warn('InPlay max multiple bet value is undefined');
        }

        const maxMultiple =
            marketModel.betLimitsMap?.inPlay.maxMultipleBet !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.inPlay.maxMultipleBet).value
                : '0';
        return this.inPlayMaxMultipleBetValue !== null ? this.inPlayMaxMultipleBetValue : maxMultiple;
    }

    @action onChangeInPlayMaxMultipleBet = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.inPlayMaxMultipleBetError = isValid(value);
        this.inPlayMaxMultipleBetValue = event.target.value;
    };

    @action onSaveInPlayMaxMultipleBet = (): void => {
        if (this.inPlayMaxMultipleBetError !== null || this.inPlayMaxMultipleBetValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                inPlay: {
                    maxMultipleBet: moneyForPayload(this.inPlayMaxMultipleBetValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };

    @computed get inPlayMaxWin(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        if (marketModel.betLimitsMap?.show.maxWin === undefined) {
            console.warn('InPlay max win value is undefined');
        }

        const maxWin =
            marketModel.betLimitsMap?.inPlay.maxWin !== undefined
                ? $appState.configState.precision.newFromOld(marketModel.betLimitsMap?.inPlay.maxWin).value
                : '0';
        return this.inPlayMaxWinValue !== null ? this.inPlayMaxWinValue : maxWin;
    }

    @action onChangeInPlayMaxWin = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        this.inPlayMaxWinError = isValid(value);
        this.inPlayMaxWinValue = event.target.value;
    };

    @action onSaveInPlayMaxWin = (): void => {
        if (this.inPlayMaxWinError !== null || this.inPlayMaxWinValue === null) {
            return;
        }

        const payload = {
            betLimitsMap: {
                inPlay: {
                    maxWin: moneyForPayload(this.inPlayMaxWinValue),
                },
            },
        };

        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
    };
}
